import type { AdminSession, AdminState } from "./types";

const REMOTE_API = import.meta.env.VITE_ADMIN_API_URL as string | undefined;
const API = REMOTE_API?.replace(/\/$/u, "") ?? "/api/admin";

export interface AdminOtpRequest {
  readonly expiresAt: number;
  readonly resendAt: number;
  readonly developmentCode?: string;
}

async function json<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = (await response
      .json()
      .catch(() => ({ message: "管理服务请求失败" }))) as {
      message?: string;
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? body.message ?? "管理服务请求失败");
  }
  return (await response.json()) as T;
}

export const adminApi = {
  async requestOtp(email: string, captchaToken?: string) {
    return await json<AdminOtpRequest>(
      await fetch(endpoint("/otp/request", "/auth/otp/request"), {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          email,
          ...(captchaToken ? { captchaToken } : {}),
        }),
      }),
    );
  },
  async verifyOtp(email: string, code: string) {
    const result = await json<{
      sessionToken: string;
      session: { email: string };
    }>(
      await fetch(endpoint("/otp/verify", "/auth/otp/verify"), {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, code }),
      }),
    );
    await json(
      await fetch(endpoint("/authorize", "/admin/authorize"), {
        headers: { authorization: `Bearer ${result.sessionToken}` },
      }),
    );
    return {
      token: result.sessionToken,
      email: result.session.email,
    } satisfies AdminSession;
  },
  async state(token: string) {
    return await json<AdminState>(
      await fetch(endpoint("/state", "/admin/state"), {
        headers: { authorization: `Bearer ${token}` },
      }),
    );
  },
  async revoke(token: string) {
    const response = await fetch(endpoint("/session", "/auth/session"), {
      method: "DELETE",
      headers: { authorization: `Bearer ${token}` },
      keepalive: true,
    });
    if (!response.ok) throw new Error("服务端会话注销失败");
  },
  async action(
    token: string,
    action: string,
    targetId: string,
    payload: Record<string, string | number | boolean> = {},
  ) {
    return await json<AdminState>(
      await fetch(endpoint("/action", "/admin/action"), {
        method: "POST",
        headers: {
          authorization: `Bearer ${token}`,
          "content-type": "application/json",
        },
        body: JSON.stringify({ action, targetId, payload }),
      }),
    );
  },
};

function endpoint(localPath: string, remotePath: string): string {
  return `${API}${REMOTE_API ? remotePath : localPath}`;
}
